import { useFormik } from "formik";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { PlanItemsMenuType, PlanItemsType } from "./components/PlanSelect";
import {
	CloseButton,
	MainTitle,
	Content,
	ContentItemWrapper,
	ContentTitle,
	ContentBox,
	Wrapper,
	OrganizationWrapper,
	OrganizationTitle,
	OrganizationTopWrapper,
	OrganizationPlanTitle,
	OrganizationPlanAmount,
	OrganizationPrice,
	OrganizationPriceDate,
	PlanSelectTitle,
	StyledPlanSelect,
	PlanEffectiveWrapper,
	PlanEffectiveCategory,
	PlanEffectiveContentWrapper,
	PlanEffectiveContentTitle,
	PlanEffectiveContentSubtitle,
	PlanChangeButton,
	PlanTableWrapper,
	TableTdLink,
	ContactUsGuideWrapper,
	ContactUsGuideContent,
	ContactUsSendButton,
	SectionTitle,
	ModalTitle,
} from "./style";
import CloseIcon from "@/assets/img/ic_modal_close.svg";
import IamportLoader, { IamportLoaderRefType } from "@/components/IamportLoader";
import Table from "@/components/Table/Table";
import { checkAuth } from "@/hooks/checkAuth";
import { useDispatch, useSelector } from "react-redux";
import { setIsLoading } from "@/redux/global/actions";
import { getIsLoading } from "@/redux/global/selector";
import Loader from "@/components/Loader";
import { getPaymentPlan, postCompanySubscribe, postContactUs } from "@/views/Price/api";
import axios from "axios";
import { useSnackbar } from "notistack";
import {
	RequestPayResponseCallback,
	RequestPayResponseSuccess,
} from "@/components/IamportLoader/types";
import { capitalize } from "@/utils/global";
import { PaymentPlanResponseType } from "@/views/Price/types";
import { formatCash, numberWithCommas } from "@/utils/number";
import { getCompanyInfo, getCurrentPlan } from "@/redux/organization/selector";
import useOrganizationInfoRequestThunk from "@/hooks/Organization/useOrganizationInfoRequestThunk";
import { PlanTypes } from "../../../types/plan";
import getOrganizationInfoRequestThunk from "@/redux/organization/thunk";
import useGetUserInfoRequest from "@/hooks/user/useGetUserInfoRequest";
import Input from "@/components/Input";
import Modal from "@/components/Modal";
import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";

interface InitialValuesType {
	plan: number | null;
}

interface LocationType {
	addId: number;
	appName: string;
	isUse: boolean;
}

function Price() {
	checkAuth();
	useOrganizationInfoRequestThunk();
	useGetUserInfoRequest();
	const { t, i18n } = useTranslation();
	const curLanguage = i18n.language;
	const history = useHistory();
	const location: LocationType = history.location.state as LocationType;
	const dispatch = useDispatch();
	const currentPlan = useSelector(getCurrentPlan);
	const companyInfo = useSelector(getCompanyInfo);
	const isLoading = useSelector(getIsLoading);
	const { enqueueSnackbar } = useSnackbar();

	const [planItem, setPlanItem] = useState<
		Array<PlanItemsType<PaymentPlanResponseType>>
	>([]);

	const sendMailInputRef = useRef<HTMLInputElement>(null);
	const [sendMail, setSendMail] = useState<string | undefined>("");
	const titleRef = useRef<HTMLInputElement>(null);
	const contentRef = useRef<HTMLInputElement>(null);
	const [isOpenMailChangeModal, setIsOpenMailChangeModal] = useState<boolean>(false);

	const Iamport = useRef<IamportLoaderRefType>(null);

	const handleClickClose = () => history.go(-1);

	const initialValues: InitialValuesType = {
		plan: null,
	};

	const { getFieldProps, values } = useFormik<InitialValuesType>({
		initialValues,
		onSubmit: () => {
			//
			console.log("테스트 메일 발송");
		},
	});

	const selectedItem: PlanItemsMenuType | undefined = useMemo(
		() =>
			planItem.find(
				(item) => item.type === "item" && item.id === values.plan,
			) as PlanItemsMenuType,
		[values.plan],
	);

	const fetchData = async () => {
		dispatch(setIsLoading(true));
		try {
			const planData = await getPaymentPlan();
			const result: Array<PlanItemsType<PaymentPlanResponseType>> = [];
			const planTypes: PlanTypes[] = planData
				.map((v) => v.planType)
				.filter((item, index, arr) => arr.indexOf(item) === index);

			planTypes.forEach((planType) => {
				const findItems = planData.filter((v) => v.planType === planType);

				result.push({
					type: "title",
					value: capitalize(planType) as Capitalize<PlanTypes>,
				});

				findItems.forEach((v) => {
					result.push({
						type: "item",
						value: v.name,
						dateUnit: v.termType,
						id: v.id,
						price: `₩ ${numberWithCommas(v.price)}`,
						title: `${v.name} ${formatCash(v.apiCount)}`,
						subtitle: `${numberWithCommas(v.apiCount)} Calls`,
						originalValue: v,
					});
				});
			});
			setPlanItem(result);
		} catch (e) {
			if (axios.isAxiosError(e)) {
				enqueueSnackbar("현재 플랜정보를 불러오는 중 오류가 발생했습니다.", {
					variant: "error",
				});
			}
		}

		dispatch(setIsLoading(false));
	};

	const checkEmail = () => {
		if (sendMail == null) {
			return;
		}
		const regExp =
			/^[0-9a-zA-Z]([-_\\.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_\\.]?[0-9a-zA-Z])*\.[a-zA-Z]{2,3}$/i;
		return regExp.test(sendMail);
	};

	const sendEmail = async () => {
		if (sendMail === "" || sendMail == null) {
			enqueueSnackbar(t("price.send_mail_empty"), {
				variant: "info",
			});
			return;
		}
		if (!checkEmail()) {
			enqueueSnackbar(t("price.send_mail_form"), {
				variant: "info",
			});
			return;
		}
		if (contentRef.current == null || titleRef.current == null) {
			return;
		}
		if (titleRef.current.value === "") {
			enqueueSnackbar(t("price.send_mail_title"), {
				variant: "info",
			});
			return;
		}
		if (contentRef.current.value === "") {
			enqueueSnackbar(t("price.send_mail_contents"), {
				variant: "info",
			});
			return;
		}
		try {
			await postContactUs({
				contents: contentRef.current.value,
				title: titleRef.current.value,
				email: sendMail,
				manager: "",
				type: "premium",
			});
			enqueueSnackbar(t("price.send_mail_success"), {
				variant: "success",
			});
		} catch (e) {
			enqueueSnackbar("메일 발송중 오류가 발생했습니다.", {
				variant: "error",
			});
		}
	};

	const handleClickChangePlan = async () => {
		if (!window.IMP || !values.plan || !Iamport.current) {
			return;
		}

		try {
			const paymentItem = planItem.find(
				(v) => v.type === "item" && v.id === values.plan,
			) as PlanItemsMenuType<PaymentPlanResponseType>;

			Iamport.current.requestPay({
				params: {
					// for Test
					// pg: "html5_inicis_INIBillTst", // 실제 계약 후에는 실제 상점아이디로 변경
					// for Prod
					pg:
						process.env.REACT_APP_MODE === "development"
							? "html5_inicis_INIBillTst" // for Test
							: "html5_inicis.MOIdobill0", // for Prod,실제 계약 후에는 실제 상점아이디로 변경
					pay_method: "card",
					merchant_uid: `payment-${new Date().toISOString()}`,
					name: paymentItem.title,
					amount: paymentItem.originalValue?.price || 0,
					buyer_tel: "01012341234",
				},
				planId: values.plan,
				callback: handleImpRequestCallback,
			});
		} catch (e) {
			if (axios.isAxiosError(e)) {
				enqueueSnackbar("정기결제 Unique Key 발급 오류.", {
					variant: "error",
				});
			}
		}
	};

	const handleImpRequestCallback: RequestPayResponseCallback = async (payload) => {
		if (!payload.success) {
			enqueueSnackbar(payload.error_msg, {
				variant: "error",
			});
			return;
		}

		const {
			card_name,
			card_number,
			custom_data,
			customer_uid,
			imp_uid,
			merchant_uid,
			pg_tid,
			paid_amount,
			paid_at,
			success,
		} = payload as RequestPayResponseSuccess;

		try {
			await postCompanySubscribe({
				cardName: card_name,
				cardNumber: card_number,
				customData: custom_data,
				customerUid: customer_uid,
				impUid: imp_uid,
				merchantUid: merchant_uid,
				paidAmount: paid_amount,
				paidAt: paid_at,
				pgTid: pg_tid,
				success,
				spec: JSON.stringify(payload),
			});

			dispatch(getOrganizationInfoRequestThunk());

			enqueueSnackbar("플랜 변경 성공.", {
				variant: "success",
			});
		} catch (e) {
			if (axios.isAxiosError(e)) {
				enqueueSnackbar("정기결제 신청 오류.", {
					variant: "error",
				});
			}
		}
	};

	const handleCloseMailChangeModal = () => {
		setIsOpenMailChangeModal(false);
	};

	const handleClickMailChangeButton = () => {
		setSendMail(sendMailInputRef.current?.value);
		setIsOpenMailChangeModal(false);
	};

	useEffect(() => {
		fetchData();
	}, []);

	useEffect(() => {
		if (companyInfo != null) {
			setSendMail(companyInfo.email);
		}
	}, [companyInfo]);

	if (!currentPlan || !companyInfo) {
		return <Loader />;
	}

	return (
		<IamportLoader ref={Iamport}>
			<Wrapper>
				<CloseButton size="large" onClick={handleClickClose}>
					<img src={CloseIcon} alt="close" />
				</CloseButton>
				{isLoading ? (
					<Loader />
				) : (
					<Content>
						<MainTitle>{t("price.title")}</MainTitle>

						<ContentItemWrapper>
							{/* <ContentTitle>결제 세부정보</ContentTitle> */}
							<ContentTitle>{t("price.contact_us_title")}</ContentTitle>

							<ContentBox>
								{/* <OrganizationWrapper>
									<OrganizationTopWrapper>
										<OrganizationTitle>
											{companyInfo.name}
										</OrganizationTitle>
										<OrganizationPlanTitle>
											{currentPlan.paymentPlan?.title ||
												"플랜 등록 필요"}
										</OrganizationPlanTitle>
										<OrganizationPlanAmount>
											{currentPlan.paymentPlan?.apiCountTotal &&
											currentPlan.paymentPlan.apiCountTotal >= 0
												? `${numberWithCommas(
														currentPlan.paymentPlan
															.apiCountTotal,
												  )} Calls`
												: "무제한"}
										</OrganizationPlanAmount>
									</OrganizationTopWrapper>

									{currentPlan.paymentPlan && (
										<>
											<OrganizationPrice>
												₩{" "}
												{numberWithCommas(
													currentPlan.paymentPlan?.price,
												)}
												<OrganizationPriceDate>
													/ 월
												</OrganizationPriceDate>
											</OrganizationPrice>
										</>
									)}
								</OrganizationWrapper> */}
								<OrganizationWrapper>
									<OrganizationTopWrapper>
										<OrganizationTitle>
											{t("application_title")}
										</OrganizationTitle>
										<OrganizationPlanTitle>
											{location ? location.appName : ""}
										</OrganizationPlanTitle>
										{!location.isUse && (
											<span
												style={{
													backgroundColor: "#e9e9e9",
													padding: "5px 8px",
													borderRadius: "4px",
												}}
											>
												{t("pending_approval")}
											</span>
										)}
									</OrganizationTopWrapper>
								</OrganizationWrapper>

								<ContactUsGuideWrapper>
									<ContactUsGuideContent>
										<div style={{ marginBottom: "1.5rem" }}>
											<p
												style={{
													fontSize: "14px",
													lineHeight: "24px",
													color: "black",
												}}
											>
												{t("price.mail_label")}
											</p>
											<div
												style={{
													display: "flex",
													gap: "0.25rem",
												}}
											>
												<div
													style={{
														width: "100%",
														height: "auto",
														backgroundColor: "#e8e8e8",
														borderRadius: "0.25rem",
														display: "flex",
														alignItems: "center",
														paddingLeft: "1.1rem",
													}}
												>
													{sendMail}
												</div>
												<Button
													sx={{
														width: "5.625rem",
														height: "40px",
													}}
													variant="contained"
													onClick={() =>
														setIsOpenMailChangeModal(true)
													}
												>
													{t("price.mail_change")}
												</Button>
											</div>
											<Modal
												titleText="이메일 변경"
												handleClose={handleCloseMailChangeModal}
												open={isOpenMailChangeModal}
												cancelButtonText="취소"
												submitButtonText={t("price.mail_change")}
												handleClickCancelButton={
													handleCloseMailChangeModal
												}
												handleClickSubmitButton={
													handleClickMailChangeButton
												}
											>
												<>
													<ModalTitle>
														{t("price.mail_label")}
													</ModalTitle>
													<Input
														inputRef={sendMailInputRef}
														placeholder="변경할 메일 주소를 입력해주세요."
														defaultValue={sendMail}
													/>
												</>
											</Modal>
										</div>
										<Input
											inputRef={titleRef}
											placeholder={t("price.title_placeholder")}
											titleText={t("price.title_label")}
											marginBottom={20}
										/>
										<Input
											inputRef={contentRef}
											placeholder={t("price.contents_placeholder")}
											titleText={t("price.contents_label")}
											marginBottom={20}
											rows={5}
										/>
										{/* <ContactUsSendButton
											variant="contained"
											backgroundColor="#0071E3"
											// disabled={!values.title || !values.content || isSubmitting}
											type="submit"
											height={40}
											// loading={isSubmitting}
										>
											결제하기
										</ContactUsSendButton> */}
										<ContactUsSendButton
											variant="contained"
											backgroundColor="#0071E3"
											// disabled={!values.title || !values.content || isSubmitting}
											height={40}
											// loading={isSubmitting}
											onClick={() => sendEmail()}
										>
											{t("price.send_mail")}
										</ContactUsSendButton>
									</ContactUsGuideContent>
									{/* <PlanSelectTitle>플랜 변경</PlanSelectTitle>
									<StyledPlanSelect
										{...getFieldProps("plan")}
										selectedItem={selectedItem}
										items={planItem}
										height={94}
										placeholder="선택하세요"
									/>

									<PlanEffectiveWrapper>
										<PlanEffectiveCategory>
											적용시점
										</PlanEffectiveCategory>

										<PlanEffectiveContentWrapper>
											<PlanEffectiveContentTitle>
												무료 체험판 종료 후 즉시
											</PlanEffectiveContentTitle>
											<PlanEffectiveContentSubtitle>
												원하시는 플랜을 선택 후 결제를 진행하시면
												무료 체험판이 종료되는 즉시 결제 주기가
												시작됩니다.
												<br />
												해당 상품은 매월 자동결제됩니다.
											</PlanEffectiveContentSubtitle>
										</PlanEffectiveContentWrapper>
									</PlanEffectiveWrapper>

									<PlanChangeButton
										variant="contained"
										backgroundColor="#2A1BDA"
										onClick={handleClickChangePlan}
										disabled={!values.plan}
									>
										결제하기
									</PlanChangeButton> */}
								</ContactUsGuideWrapper>
							</ContentBox>
						</ContentItemWrapper>

						<SectionTitle>{t("price.foodlens_title")}</SectionTitle>
						<ContentItemWrapper>
							{/* <ContentTitle>Plan and Pricing</ContentTitle> */}
							<ContentTitle>{t("price.plan_price_title")}</ContentTitle>

							<ContentBox>
								<PlanTableWrapper>
									<Table
										col={[50, 25, 25]}
										headItems={["", "Basic", "Custom"]}
										rowItems={[
											[
												"월별금액",
												curLanguage === "ko"
													? "300만원"
													: "USD $3,000/month",
												<span
													style={{
														textDecoration: "underline",
														cursor: "pointer",
														color: "#2a1bda",
													}}
													onClick={() => {
														window.scrollTo({
															top: 0,
															left: 0,
															behavior: "smooth",
														});
													}}
												>
													Talk To Sales
												</span>,
											],
										]}
										headProps={[
											{ padding: [0, 0, 20, 12] },
											{
												textAlign: "center",
												padding: [0, 0, 20, 0],
											},
											{
												textAlign: "center",
												padding: [0, 0, 20, 0],
											},
										]}
										rowProps={[
											{ padding: [10, 0, 10, 12] },
											{ textAlign: "center", padding: [10, 0] },
											{ textAlign: "center", padding: [10, 0] },
										]}
									/>
								</PlanTableWrapper>
							</ContentBox>
						</ContentItemWrapper>

						<ContentItemWrapper>
							{/* <ContentTitle>Features</ContentTitle> */}
							<ContentTitle>{t("price.plan_feature_title")}</ContentTitle>
							<ContentBox>
								<PlanTableWrapper>
									<Table
										col={[50, 25, 25]}
										headItems={["Usage", "Basic", "Custom"]}
										rowItems={[
											[
												"CALL",
												"50,000 Server Call",
												<span
													style={{
														textDecoration: "underline",
														cursor: "pointer",
														color: "#2a1bda",
													}}
													onClick={() => {
														window.scrollTo({
															top: 0,
															left: 0,
															behavior: "smooth",
														});
													}}
												>
													{curLanguage === "ko"
														? "커스텀 서버 콜"
														: "Custom Server Call"}
												</span>,
											],
											[
												curLanguage === "ko"
													? "음식 인식 방식"
													: "Food recognition method",
												curLanguage === "ko"
													? "음식명 인식"
													: "Material Recognition",
												curLanguage === "ko"
													? "음식명 인식"
													: "Material Recognition",
											],
											[
												curLanguage === "ko"
													? "영양소 정보"
													: "Nutrient information",
												curLanguage === "ko"
													? "대량 영양소 (탄수화물, 단백질, 지방) 정보제공"
													: "Bulk Nutrient (Carbohydrate, Protein, Fat)\nInformation Provision",
												curLanguage === "ko"
													? "대량 영양소 (탄수화물, 단백질, 지방) 정보제공"
													: "Bulk Nutrient (Carbohydrate, Protein, Fat)\nInformation Provision",
											],
											[
												curLanguage === "ko"
													? "칼로리 정보"
													: "Calorie information",
												curLanguage === "ko"
													? "Kcal 권장정보 제공"
													: "Kcal Information Provision",
												curLanguage === "ko"
													? "Kcal 권장정보 제공"
													: "Kcal Information Provision",
											],
											[
												"UI Kit",
												curLanguage === "ko"
													? "Sample UI Kit 제공"
													: "Sample UI Kit Provided",
												curLanguage === "ko"
													? "Sample UI Kit 제공"
													: "Sample UI Kit Provided",
											],
											[
												curLanguage === "ko" ? "기타" : "Others",
												"-",
												curLanguage === "ko"
													? "최우선 고객 지원"
													: "Priority Customer Support",
											],
										]}
										headProps={[
											{ padding: [0, 0, 20, 12] },
											{
												textAlign: "center",
												padding: [0, 0, 20, 0],
											},
											{
												textAlign: "center",
												padding: [0, 0, 20, 0],
											},
										]}
										rowProps={[
											{ padding: [10, 0, 10, 12] },
											{ textAlign: "center", padding: [10, 40] },
											{ textAlign: "center", padding: [10, 40] },
										]}
									/>
								</PlanTableWrapper>
							</ContentBox>
						</ContentItemWrapper>

						{/*  */}
						<SectionTitle>{t("price.caloai_title")}</SectionTitle>
						<ContentItemWrapper>
							{/* <ContentTitle>Plan and Pricing</ContentTitle> */}
							<ContentTitle>{t("price.plan_price_title")}</ContentTitle>

							<ContentBox>
								<PlanTableWrapper>
									<Table
										col={[25, 25, 25, 25]}
										headItems={[
											"",
											curLanguage === "ko"
												? "사용 Device 비례 플랜"
												: "Device Based Pricing",
											curLanguage === "ko"
												? "사용량 비례 플랜"
												: "Usage Based Pricing",
											curLanguage === "ko"
												? "커스텀 플랜"
												: "Custom",
										]}
										rowItems={[
											[
												curLanguage === "ko" ? "가격" : "Price",
												curLanguage === "ko"
													? "1,500원/device/월(부가세 별도)"
													: "USD $1.5/device/month",
												curLanguage === "ko"
													? "300만원/월(부가세 별도)"
													: "USD $3,000/month",
												// <TableTdLink to="/organization/contact">
												// 	요금 별도 문의 및 협의
												// </TableTdLink>,
												<span
													style={{
														textDecoration: "underline",
														cursor: "pointer",
														color: "#2a1bda",
													}}
													onClick={() => {
														window.scrollTo({
															top: 0,
															left: 0,
															behavior: "smooth",
														});
													}}
												>
													{curLanguage === "ko"
														? "요금 별도 문의 및 협의"
														: "Please inquire separately about the fees"}
												</span>,
											],
										]}
										headProps={[
											{ padding: [0, 0, 20, 12] },
											{
												textAlign: "center",
												padding: [0, 0, 20, 0],
											},
											{
												textAlign: "center",
												padding: [0, 0, 20, 0],
											},
											{
												textAlign: "center",
												padding: [0, 0, 20, 0],
											},
										]}
										rowProps={[
											{ padding: [10, 0, 10, 12] },
											{ textAlign: "center", padding: [10, 0] },
											{ textAlign: "center", padding: [10, 0] },
											{ textAlign: "center", padding: [10, 0] },
										]}
									/>
								</PlanTableWrapper>
							</ContentBox>
						</ContentItemWrapper>

						<ContentItemWrapper>
							{/* <ContentTitle>Features</ContentTitle> */}
							<ContentTitle>{t("price.plan_feature_title")}</ContentTitle>
							<ContentBox>
								<PlanTableWrapper>
									<Table
										col={[25, 25, 25, 25]}
										headItems={[
											"Usage",
											curLanguage === "ko"
												? "사용 Device 비례 플랜"
												: "Device Based Pricing",
											curLanguage === "ko"
												? "사용량 비례 플랜"
												: "Usage Based Pricing",
											curLanguage === "ko"
												? "커스텀 플랜"
												: "Custom",
										]}
										rowItems={[
											[
												"CALL",
												curLanguage === "ko"
													? "무제한 Server Calls"
													: "Unlimited Server Calls",
												curLanguage === "ko"
													? "30,000 Server Calls/월"
													: "30,000 Server Calls per month",
												// <TableTdLink to="/organization/contact">
												// 	커스텀 서버 콜
												// </TableTdLink>,
												<span
													style={{
														textDecoration: "underline",
														cursor: "pointer",
														color: "#2a1bda",
													}}
													onClick={() => {
														window.scrollTo({
															top: 0,
															left: 0,
															behavior: "smooth",
														});
													}}
												>
													{curLanguage === "ko"
														? "커스텀 서버 콜"
														: "Custom Server Calls"}
												</span>,
											],
											[
												curLanguage === "ko"
													? "음식 인식 방식"
													: "Food recognition method",
												curLanguage === "ko"
													? "재료 인식"
													: "Material Recognition",
												curLanguage === "ko"
													? "재료 인식"
													: "Material Recognition",
												curLanguage === "ko"
													? "재료 인식"
													: "Material Recognition",
											],
											[
												curLanguage === "ko"
													? "영양소 정보"
													: "Nutrient information",
												curLanguage === "ko"
													? "대량 영양소 (탄수화물, 단백질, 지방) 정보제공"
													: "Bulk Nutrient (Carbohydrate, Protein, Fat)\nInformation Provision",
												curLanguage === "ko"
													? "대량영양소(탄수화물, 단백질, 지방 등), 미량영양소 포함 총 36종 정보 제공"
													: "Bulk Nutrient Information\n(Carbohydrate, Protein, Fat, etc.)\nTotal of 36 types of information,\nincluding trace nutrients",
												curLanguage === "ko"
													? "대량 영양소 (탄수화물, 단백질, 지방) 정보제공"
													: "Bulk Nutrient (Carbohydrate, Protein, Fat)\nInformation Provision",
											],
											[
												curLanguage === "ko"
													? "칼로리 정보"
													: "Calorie information",
												curLanguage === "ko"
													? "Kcal 정보 제공"
													: "Kcal Information Provision",
												curLanguage === "ko"
													? "Kcal 정보 제공"
													: "Kcal Information Provision",
												curLanguage === "ko"
													? "Kcal 정보 제공"
													: "Kcal Information Provision",
											],
											[
												"UI Kit",
												curLanguage === "ko"
													? "Sample UI Kit 제공"
													: "Sample UI Kit Provided",
												curLanguage === "ko"
													? "Sample UI Kit 제공"
													: "Sample UI Kit Provided",
												curLanguage === "ko"
													? "Sample UI Kit 제공"
													: "Sample UI Kit Provided",
											],
											[
												curLanguage === "ko" ? "기타" : "Others",
												curLanguage === "ko"
													? "사용 증가 예상 시 커스텀 플랜 협의 가능"
													: "Custom plan negotiation possible with\nexpected usage increase",
												curLanguage === "ko"
													? "사용 증가 예상 시 커스텀 플랜 협의 가능"
													: "Custom plan negotiation possible\nwith expected usage increase.",
												curLanguage === "ko"
													? "최우선 고객 지원\nBasic 플랜 대비 할인 적용"
													: "Priority Customer Support Discounts\napplied compared to Basic plan",
											],
										]}
										headProps={[
											{ padding: [0, 0, 20, 12] },
											{
												textAlign: "center",
												padding: [0, 0, 20, 0],
											},
											{
												textAlign: "center",
												padding: [0, 0, 20, 0],
											},
											{
												textAlign: "center",
												padding: [0, 0, 20, 0],
											},
										]}
										rowProps={[
											{ padding: [10, 0, 10, 12] },
											{ textAlign: "center", padding: [10, 20] },
											{ textAlign: "center", padding: [10, 20] },
											{ textAlign: "center", padding: [10, 20] },
										]}
									/>
								</PlanTableWrapper>
							</ContentBox>
						</ContentItemWrapper>
						{/*  */}
					</Content>
				)}
			</Wrapper>
		</IamportLoader>
	);
}

export default Price;
